import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AudioRecorder } from '../components/AudioRecorder';

export default function DemoRecordingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [userName, setUserName] = useState('');
  const [recordingComplete, setRecordingComplete] = useState(false);

  const loadSessionData = useCallback(async () => {
    try {
      const sessionDoc = await getDoc(doc(db, 'demoSessions', sessionId));

      if (!sessionDoc.exists()) {
        setError('Demo session not found');
        setLoading(false);
        return;
      }

      const session = sessionDoc.data();

      // Check if expired
      if (session.expiresAt.toMillis() < Date.now()) {
        setError('This demo session has expired');
        setLoading(false);
        return;
      }

      // Check if reached recording limit
      if (session.recordingCount >= session.maxRecordings) {
        setError('limit-reached');
        setLoading(false);
        return;
      }

      setSessionData(session);

      // Load user name
      const userDoc = await getDoc(doc(db, 'demoUsers', session.userId));
      if (userDoc.exists()) {
        setUserName(userDoc.data().name);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Failed to load demo session');
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    loadSessionData();
  }, [sessionId, loadSessionData]);

  const handleRecordingComplete = () => {
    setRecordingComplete(true);
  };

  const handleStartMemoryStream = () => {
    navigate('/signup');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-[#C87400] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your demo session...</p>
        </div>
      </div>
    );
  }

  if (error === 'limit-reached') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-[#C87400]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>

          <h2 className="font-serif text-2xl font-semibold text-gray-900 mb-4">
            Ready for the Full Experience?
          </h2>

          <p className="text-gray-600 mb-6 leading-relaxed">
            It looks like you might be ready to start recording your story. Purchase Memory Stream to continue preserving your memories.
          </p>

          <button
            onClick={handleStartMemoryStream}
            className="w-full bg-[#C87400] hover:bg-[#B06600] text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 text-lg mb-4"
          >
            Start Your Memory Stream
          </button>

          <button
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Return to home
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>

          <h2 className="font-serif text-2xl font-semibold text-gray-900 mb-4">
            {error === 'This demo session has expired' ? 'Session Expired' : 'Error'}
          </h2>

          <p className="text-gray-600 mb-6">
            {error}
          </p>

          <button
            onClick={() => navigate('/demo')}
            className="w-full bg-[#C87400] hover:bg-[#B06600] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Request New Demo
          </button>
        </div>
      </div>
    );
  }

  const question = {
    id: 'demo-question',
    text: `${userName}, tell me about the city you grew up in. What are some of your favorite memories of that place?`
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Warning Banner */}
        <div className="bg-orange-50 border-l-4 border-[#C87400] p-4 mb-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-[#C87400] mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-[#061D3D]">
                🎙️ Demo recordings aren't saved. Start your Memory Stream to preserve your stories forever.
              </p>
            </div>
          </div>
        </div>

        {/* Recording Counter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Recording {sessionData.recordingCount + 1} of {sessionData.maxRecordings}
            </span>
            <span className="text-sm text-gray-500">
              Session expires in {Math.floor((sessionData.expiresAt.toMillis() - Date.now()) / (1000 * 60 * 60))} hours
            </span>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-[#061D3D] text-center mb-6">
            {question.text}
          </h2>

          <AudioRecorder
            storyId="demo"
            questionId="demo-question"
            question={question}
            existingAnswers={[]}
            onAnswerComplete={handleRecordingComplete}
            useSteppedInterface={true}
            demoMode={true}
            demoSessionId={sessionId}
          />
        </div>

        {/* Post-Recording CTA */}
        {recordingComplete && (
          <div className="bg-gradient-to-r from-[#061D3D] to-[#0A2A52] rounded-lg shadow-lg p-8 text-center text-white">
            <h3 className="font-serif text-2xl font-semibold mb-3">
              Loved the experience?
            </h3>
            <p className="text-lg mb-6 text-gray-200">
              Start your Memory Stream to keep your memories forever.
            </p>
            <button
              onClick={handleStartMemoryStream}
              className="bg-[#C87400] hover:bg-[#B06600] text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-200 text-lg inline-block"
            >
              Start Your Memory Stream
            </button>
          </div>
        )}

        {/* Share Demo */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-3">
            Know someone who'd love this?
          </p>
          <button
            onClick={() => navigate('/demo')}
            className="text-[#C87400] hover:text-[#B06600] font-medium text-sm"
          >
            Share Demo Link →
          </button>
        </div>
      </div>
    </div>
  );
}
